import { useState } from "react";
import { TOKENS } from "@/constants/campaignBuilder";
import type { SubjectAtMention } from "@/interfaces/emailPreview";

/** Any field this hook can drive — it only reads value/selectionStart and moves the caret. */
type AtMentionField = HTMLInputElement | HTMLTextAreaElement;

/**
 * Encapsulates the `@mention` token-insertion logic for a single-line-ish text field.
 * Used by MessagePanel and ReminderCustomizePanel (subject lines) and by
 * LandingMessagePanel (headline + subtitle).
 *
 * @param inputRef   - ref to the <input> or <textarea> element being personalized
 * @param onChange   - called with the new raw string value whenever the field changes
 */
export function useSubjectAtMention(
  inputRef: React.RefObject<AtMentionField | null>,
  onChange: (value: string) => void,
  maxLength?: number,
  // The tag list this field offers. Defaults to the full set; manual-send fields pass
  // `availableTokens(true)` so @-mention can't autocomplete a recipient-only tag the dropdown
  // no longer shows (the two MUST agree — see availableTokens / prototype `_availableMergeTags`).
  tokens: typeof TOKENS = TOKENS,
) {
  const [subjectAtMention, setSubjectAtMention] = useState<SubjectAtMention>({
    active: false,
    startIndex: 0,
    items: [],
    activeIdx: 0,
  });

  function handleSubjectChange(e: React.ChangeEvent<AtMentionField>) {
    const val = e.target.value;
    onChange(val);

    const cursor = e.target.selectionStart ?? val.length;

    if (!subjectAtMention.active) {
      if (cursor > 0 && val.charAt(cursor - 1) === "@") {
        setSubjectAtMention({ active: true, startIndex: cursor - 1, items: tokens, activeIdx: 0 });
      }
    } else {
      if (cursor <= subjectAtMention.startIndex) {
        setSubjectAtMention((prev) => ({ ...prev, active: false }));
        return;
      }
      const query = val.slice(subjectAtMention.startIndex + 1, cursor);
      if (/\s/.test(query)) {
        setSubjectAtMention((prev) => ({ ...prev, active: false }));
        return;
      }
      const q = query.toLowerCase();
      const filtered = tokens.filter((t) => {
        const tagLow = t.tag.slice(1).toLowerCase();
        const labelLow = t.label.toLowerCase().replace(/\s+/g, "");
        return tagLow.startsWith(q) || labelLow.startsWith(q);
      });
      setSubjectAtMention((prev) => ({
        ...prev,
        items: filtered,
        activeIdx: prev.activeIdx >= filtered.length ? 0 : prev.activeIdx,
        active: filtered.length > 0,
      }));
    }
  }

  function handleSubjectKeyDown(e: React.KeyboardEvent<AtMentionField>) {
    if (!subjectAtMention.active || subjectAtMention.items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSubjectAtMention((prev) => ({
        ...prev,
        activeIdx: (prev.activeIdx + 1) % prev.items.length,
      }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSubjectAtMention((prev) => ({
        ...prev,
        activeIdx: (prev.activeIdx - 1 + prev.items.length) % prev.items.length,
      }));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const token = subjectAtMention.items[subjectAtMention.activeIdx];
      if (token) insertSubjectToken(token);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setSubjectAtMention((prev) => ({ ...prev, active: false }));
    }
  }

  function insertSubjectToken(token: (typeof TOKENS)[number]) {
    const input = inputRef.current;
    if (!input) return;
    const currentValue = input.value;
    const cursor = input.selectionStart ?? currentValue.length;
    const before = currentValue.slice(0, subjectAtMention.startIndex);
    const after = currentValue.slice(cursor);
    const inserted = `${token.tag} `;
    let next = before + inserted + after;
    if (maxLength !== undefined && next.length > maxLength) next = next.slice(0, maxLength);
    onChange(next);
    setSubjectAtMention((prev) => ({ ...prev, active: false }));
    const newCaret = subjectAtMention.startIndex + inserted.length;
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(newCaret, newCaret);
    });
  }

  function dismissAtMention() {
    setSubjectAtMention((prev) => ({ ...prev, active: false }));
  }

  return {
    subjectAtMention,
    handleSubjectChange,
    handleSubjectKeyDown,
    insertSubjectToken,
    dismissAtMention,
  };
}
